import { useBaseOpenAI, useActivityFeedbackAccess } from '@speakableio/core'
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { useState } from 'react'
import { v4 } from 'uuid'

import { storage } from '../firebase'
import { convertSafariAudio } from '../utils/convertAudioFormat'

import useIsIOS from './useIsIOS'

import { isSafari } from '@/utils/browser-type'

const isActivityRoute = () => {
  const currentRoute = typeof window !== 'undefined' ? window.location.pathname : ''

  return currentRoute.includes('/activity')
}

const useOpenAI = ({
  onAudioUploadSuccess = () => {},
  onAudioUploadError = () => {},
  onTranscriptSuccess = () => {},
  onTranscriptError = () => {},
  onCompletionSuccess = () => {},
  onCompletionError = () => {},
  audioPath = null,
  // NEW PARAMETERS for activity feedback access
  aiEnabled = false,
} = {}) => {
  const [isUploading, setIsUploading] = useState(false)

  const {
    getFeedback,
    getFreeResponseCompletion,
    getTranscript,
    submitAudioResponse: submitAudioResponseBase,
    uploadAudioAndGetTranscript: uploadAudioAndGetTranscriptBase,
  } = useBaseOpenAI({
    aiEnabled,
    onCompletionSuccess,
    onCompletionError,
    onTranscriptSuccess,
    onTranscriptError,
    onAudioUploadSuccess,
    onAudioUploadError,
    submitAudioResponse: async audio => {
      const response = await submitAudioResponse(audio)

      return {
        fileName: response?.fileName || '',
        url: response?.url || '',
      }
    },
    uploadAudioAndGetTranscript: async (audio, language) => {
      const transcript = await uploadAudioAndGetTranscript(audio, language)

      return transcript
    },
  })

  const isIos = useIsIOS()

  const { data: feedbackAccess } = useActivityFeedbackAccess({
    aiEnabled,
    isActivityRoute: isActivityRoute(),
  })

  async function submitAudioResponse(audio) {
    setIsUploading(true)
    let audioBlob = audio

    if (isIos || isSafari) {
      audioBlob = await convertSafariAudio(audio)
    }

    const id = v4()
    const fileName = audioPath ? `${audioPath}/${id}.mp3` : `freeResponse/${id}.mp3`
    const audioStorageRef = ref(storage, fileName)
    const uploadTask = uploadBytesResumable(audioStorageRef, audioBlob)

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        snapshot => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)

          console.log('Upload is ' + progress + '% done')
        },
        error => {
          console.log('uploadAudioMessage', error)
          reject(error)
          setIsUploading(false)
          onAudioUploadError({
            type: 'AUDIO',
            message: 'Error uploading audio',
          })
        },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref)

          resolve({ url, fileName })
          onAudioUploadSuccess({ url, fileName })
          setIsUploading(false)
        },
      )
    })
  }

  const uploadAudioAndGetTranscript = async (audio, language) => {
    try {
      let file
      let audioUrl

      if (typeof audio === 'string') {
        // response has already been submitted but failed to get transcript
        const audioBlob = await downloadAudioBlob(audio)
        const { fileName, url } = await submitAudioResponse(audioBlob)

        file = fileName
        audioUrl = url
      } else {
        // response has not been submitted
        const { fileName, url } = await submitAudioResponse(audio)

        file = fileName
        audioUrl = url
      }

      // NEW CODE - Use feedbackAccess instead of old permissions
      if (feedbackAccess?.canAccessFeedback) {
        const transcript = await getTranscript(audioUrl, language)

        return transcript
      } else {
        console.info(`Transcript not available: ${feedbackAccess?.reason || 'No feedback access'}`)

        return ''
      }
    } catch (error) {
      console.error('Error uploading audio or getting transcript:', error)
      throw new Error(error)
    }
  }

  return {
    submitAudioResponse: submitAudioResponseBase,
    uploadAudioAndGetTranscript: uploadAudioAndGetTranscriptBase,
    getTransctipt: getTranscript,
    getFreeResponseCompletion,
    getFeedback,
    isUploading,
  }
}

export default useOpenAI

export const downloadAudioBlob = async url => {
  const response = await fetch(url)
  const blob = await response.blob()

  return blob
}

// const detectedLanguage = await googleTranslate.detectLanguage(transcript)

// if (detectedLanguage && detectedLanguage?.split?.('-')?.[0] !== language) {
//   alert(
//     `${langs[detectedLanguage]} was detected. You must give a response in ${langs[language]}. Please try again.`,
//   )
//   onCompletionSuccess({
//     transcript,
//     summary: `Please try again in ${langs[language]}`,
//     errorMessage: `${langs[detectedLanguage]} was detected. You must give a response in ${langs[language]}. Please try again.`,
//     aiSuccess: true,
//   })

//   throw new Error('Language mismatch')
// }
